import { useEffect, useState } from 'react';
import { Link, useNavigate, useRouterState } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import {
  api,
  command,
  field,
  getObject,
  mutationHeaders,
  type Row,
  type Schema,
} from './api/client';
import { apiError } from './api/session';
import { useAccess, useRows, useWorkspace } from './api/data';
import { ActionForm } from './forms';
import { StructuredData } from './structured-data';
import {
  Select,
  Button,
  Empty,
  ErrorState,
  Field,
  FilePicker,
  Loading,
  Status,
  Table,
  Tabs,
} from './ui';

export function OperationsPage() {
  const access = useAccess();
  const navigate = useNavigate();
  const location = useRouterState({ select: (state) => state.location });
  const mode = location.pathname.split('/')[2] || 'import';
  const [operationId, setOperationId] = useState('');
  const tabs = [
    ...(access.can('candidates.import') && access.can('files.upload') && access.can('files.read')
      ? [{ label: 'Импорт кандидатов', value: 'import' }]
      : []),
    ...(access.can('exports.run') ? [{ label: 'Экспорт данных', value: 'export' }] : []),
  ];
  if (access.isLoading) return <Loading />;
  return (
    <div className="operations-page">
      <Tabs
        tabs={tabs}
        value={mode}
        onChange={(value) => void navigate({ to: '/operations/$mode', params: { mode: value } })}
      />
      {mode === 'import' && tabs.some((tab) => tab.value === 'import') ? (
        <CandidateImport />
      ) : mode === 'export' && access.can('exports.run') ? (
        <ExportForm />
      ) : (
        <Empty title="Недостаточно прав" />
      )}
      <section className="panel operation-lookup">
        <h2>Результат операции</h2>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            void navigate({ to: '/operations/result/$operationId', params: { operationId } });
          }}
        >
          <Field
            label="Номер операции"
            name="operationId"
            required
            value={operationId}
            pattern="[0-9a-fA-F-]{36}"
            onChange={(event) => setOperationId(event.target.value)}
          />
          <Button type="submit">Открыть результат</Button>
        </form>
      </section>
    </div>
  );
}
function ExportForm() {
  const navigate = useNavigate();
  const [resourceType, setResourceType] = useState('employees');
  const [format, setFormat] = useState('CSV');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  return (
    <section className="form-page">
      <h1>Экспорт данных</h1>
      <form
        className="domain-form"
        onSubmit={async (event) => {
          event.preventDefault();
          setPending(true);
          setError(null);
          try {
            const result = await command('POST', '/api/v1/exports', {
              resourceType,
              format,
              filters: {},
            });
            void navigate({
              to: '/operations/result/$operationId',
              params: { operationId: result.id },
            });
          } catch (cause) {
            setError(cause as Error);
          } finally {
            setPending(false);
          }
        }}
      >
        <Field label="Данные">
          <Select
            name="resourceType"
            value={resourceType}
            onChange={(event) => setResourceType(event.target.value)}
          >
            <option value="employees">Сотрудники</option>
            <option value="candidates">Кандидаты</option>
            <option value="applications">Отклики</option>
          </Select>
        </Field>
        <Field label="Формат">
          <Select name="format" value={format} onChange={(event) => setFormat(event.target.value)}>
            <option>CSV</option>
            <option>XLSX</option>
          </Select>
        </Field>
        {error && <ErrorState error={error} />}
        <footer>
          <Button type="submit" className="primary" disabled={pending}>
            {pending ? 'Подготовка…' : 'Создать выгрузку'}
          </Button>
        </footer>
      </form>
    </section>
  );
}
function CandidateImport() {
  const options = useQuery({
    queryKey: ['import-options'],
    queryFn: ({ signal }) =>
      getObject<{
        organizations: { id: string; name: string }[];
        processingOptions: { purpose: string; jurisdiction: string }[];
      }>('/api/v1/candidates/import-options', {}, signal),
  });
  const navigate = useNavigate();
  const [pending, setPending] = useState(false);
  const [stage, setStage] = useState('');
  const [error, setError] = useState<Error | null>(null);
  const [organizationId, setOrganizationId] = useState('');
  const [processingOption, setProcessingOption] = useState('');
  const [hasFile, setHasFile] = useState(false);
  return (
    <section className="form-page">
      <h1>Импорт кандидатов</h1>
      <p className="muted">
        Загрузите CSV или XLSX с колонками name, primaryEmail и country. Страна указывается кодом из
        двух букв. Существующие кандидаты будут пропущены.
      </p>
      <form
        className="domain-form"
        onSubmit={async (event) => {
          event.preventDefault();
          const values = new FormData(event.currentTarget);
          const file = values.get('file');
          if (!(file instanceof File) || !file.size) return;
          setPending(true);
          setError(null);
          try {
            if (file.size > 50 * 1024 * 1024) throw new Error('Размер файла превышает 50 МБ');
            const data = new FormData();
            const organizationId = String(values.get('organizationId'));
            const rule = options.data?.processingOptions[Number(values.get('processingOption'))];
            if (!rule) throw new Error('Выберите условия обработки данных');
            data.set('purpose', 'CANDIDATE_IMPORT');
            data.set('entityType', 'Organization');
            data.set('entityId', organizationId);
            data.set('file', file);
            setStage('Загрузка файла');
            const uploaded = await api.POST('/api/v1/files', {
              params: { header: mutationHeaders() },
              body: data as never,
              bodySerializer: (value) => value as unknown as FormData,
            });
            if (!uploaded.data) throw apiError(uploaded.response, uploaded.error);
            let metadata = uploaded.data;
            setStage('Проверка безопасности файла');
            for (
              let attempt = 0;
              attempt < 90 &&
              !['CLEAN', 'INFECTED', 'QUARANTINED', 'SCAN_FAILED'].includes(metadata.status);
              attempt++
            ) {
              await new Promise((resolve) => setTimeout(resolve, 1000));
              metadata = await getObject<Schema<'FileResponse'>>('/api/v1/files/{id}', {
                id: metadata.id,
              });
            }
            if (metadata.status !== 'CLEAN')
              throw new Error('Файл ещё не прошёл проверку безопасности. Повторите попытку позже.');
            setStage('Запуск импорта');
            const result = await command('POST', '/api/v1/candidates/imports', {
              fileId: metadata.id,
              organizationId,
              dryRun: values.get('dryRun') === 'on',
              source: 'IMPORT',
              purpose: rule.purpose,
              jurisdiction: rule.jurisdiction,
            });
            void navigate({
              to: '/operations/result/$operationId',
              params: { operationId: result.id },
            });
          } catch (cause) {
            setError(cause as Error);
          } finally {
            setPending(false);
          }
        }}
      >
        <Field label="Организация">
          <Select
            name="organizationId"
            required
            value={organizationId}
            onChange={(event) => setOrganizationId(event.target.value)}
          >
            <option value="" disabled>
              Выберите организацию
            </option>
            {options.data?.organizations.map((row) => (
              <option value={row.id} key={row.id}>
                {row.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Условия обработки данных">
          <Select
            name="processingOption"
            required
            value={processingOption}
            onChange={(event) => setProcessingOption(event.target.value)}
          >
            <option value="" disabled>
              Выберите условия
            </option>
            {options.data?.processingOptions.map((rule, index) => (
              <option key={index} value={index}>
                {rule.purpose} · {rule.jurisdiction}
              </option>
            ))}
          </Select>
        </Field>
        <FilePicker
          name="file"
          label="Выберите файл кандидатов"
          description="CSV, XLSX · до 50 МБ"
          accept=".csv,.xlsx"
          required
          onChange={(event) => setHasFile(Boolean(event.target.files?.[0]?.size))}
        />
        <label className="form-check">
          <input type="checkbox" name="dryRun" defaultChecked />
          Проверить файл без добавления кандидатов
        </label>
        {options.error && (
          <ErrorState error={options.error} retry={() => void options.refetch()} />
        )}{' '}
        {options.data && !options.data.processingOptions.length && (
          <div className="notice-card">
            <p>Для импорта нужны опубликованные и активные правила обработки данных.</p>
            <Link
              to="/$section/$resource"
              params={{ section: 'admin', resource: 'processing-policies' }}
            >
              Настроить правила
            </Link>
          </div>
        )}
        {pending && <p role="status">{stage}</p>}
        {error && <ErrorState error={error} />}
        <footer>
          <Button
            disabled={pending}
            onClick={() =>
              void navigate({
                to: '/$section/$resource',
                params: { section: 'recruitment', resource: 'candidates' },
              })
            }
          >
            Отмена
          </Button>
          <Button
            className="primary"
            type="submit"
            disabled={
              pending ||
              options.isLoading ||
              !organizationId ||
              processingOption === '' ||
              !hasFile ||
              !options.data?.organizations.length ||
              !options.data?.processingOptions.length
            }
          >
            {pending ? 'Подождите…' : 'Начать импорт'}
          </Button>
        </footer>
      </form>
    </section>
  );
}
export function BulkOperationPage() {
  const id = useRouterState({ select: (state) => state.location.pathname.split('/').at(-1)! });
  const operation = useQuery({
    queryKey: ['bulk-operation', id],
    queryFn: ({ signal }) => getObject<Row>('/api/v1/bulk-operations/{id}', { id }, signal),
    refetchInterval: (query) =>
      query.state.data &&
      !['COMPLETED', 'PARTIALLY_COMPLETED', 'FAILED', 'CANCELLED'].includes(
        field(query.state.data, 'status'),
      )
        ? 2000
        : false,
  });
  const terminal = Boolean(
    operation.data &&
    ['COMPLETED', 'PARTIALLY_COMPLETED', 'FAILED', 'CANCELLED'].includes(
      field(operation.data, 'status'),
    ),
  );
  const results = useRows(
    '/api/v1/bulk-operations/{id}/results',
    {},
    { params: { id }, enabled: Boolean(operation.data), refetchInterval: terminal ? false : 2000 },
  );
  const refreshResults = results.refetch;
  useEffect(() => {
    if (terminal) void refreshResults();
  }, [terminal, refreshResults]);
  const [error, setError] = useState<Error | null>(null);
  const [downloading, setDownloading] = useState(false);
  if (operation.isLoading) return <Loading />;
  if (operation.error || !operation.data) return <ErrorState error={operation.error} />;
  const row = operation.data;
  const fileId =
    row.type === 'EXPORT'
      ? field(
          results.rows.find((result) => result.resourceId),
          'resourceId',
        )
      : '';
  return (
    <section className="entity-page">
      <h1>
        {row.type === 'EXPORT'
          ? 'Экспорт данных'
          : row.type === 'CANDIDATE_IMPORT'
            ? 'Импорт кандидатов'
            : 'Проверка хранения данных'}
      </h1>
      <section className="panel">
        <div className="operation-summary">
          <Status value={field(row, 'status')} />
          <span>
            Обработано {field(row, 'processed', '0')} из {field(row, 'total', '0')}
          </span>
          <span>Ошибок: {field(row, 'failed', '0')}</span>
        </div>
        <p className="muted">Номер операции: {id}</p>
        {fileId && (
          <Button
            icon="ChevronDown"
            disabled={downloading}
            onClick={async () => {
              setDownloading(true);
              setError(null);
              try {
                const file = await getObject<Schema<'FileResponse'>>('/api/v1/files/{id}', {
                  id: fileId,
                });
                const result = await api.POST('/api/v1/files/{id}/download-url', {
                  params: { path: { id: fileId }, header: mutationHeaders() },
                  body: { fileVersionId: file.fileVersionId },
                });
                if (!result.data) throw apiError(result.response, result.error);
                const link = document.createElement('a');
                link.href = result.data.url;
                link.download = file.name;
                link.click();
              } catch (cause) {
                setError(cause as Error);
              } finally {
                setDownloading(false);
              }
            }}
          >
            Скачать файл
          </Button>
        )}
        {error && <ErrorState error={error} />}
      </section>
      <section className="panel">
        {results.error ? (
          <ErrorState error={results.error} />
        ) : (
          <Table
            rows={results.rows}
            loading={results.isLoading}
            columns={[
              { key: 'rowNumber', label: 'Строка', width: 100 },
              {
                key: 'status',
                label: 'Результат',
                width: 180,
                render: (row) => <Status value={field(row, 'status')} />,
              },
              { key: 'errorCode', label: 'Ошибка', width: 230 },
              {
                key: 'details',
                label: 'Подробности',
                width: 350,
                render: (row) => <StructuredData value={row.details} />,
              },
            ]}
            empty="Результаты появятся после обработки"
          />
        )}
        {results.hasNextPage && (
          <Button
            onClick={() => void results.fetchNextPage()}
            disabled={results.isFetchingNextPage}
          >
            Загрузить ещё
          </Button>
        )}
        <Button
          onClick={() => {
            void results.refetch();
            void operation.refetch();
          }}
        >
          Обновить
        </Button>
      </section>
      <Link to="/operations/$mode" params={{ mode: row.type === 'EXPORT' ? 'export' : 'import' }}>
        К операциям
      </Link>
    </section>
  );
}
export function WorkspaceSettingsPage() {
  const workspace = useWorkspace();
  const access = useAccess();
  const navigate = useNavigate();
  const mode = useRouterState({ select: (state) => state.location.pathname.endsWith('/edit') });
  if (workspace.isLoading) return <Loading />;
  if (workspace.error || !workspace.data) return <ErrorState error={workspace.error} />;
  const row = workspace.data;
  if (mode)
    return access.can('workspace.manage') ? (
      <ActionForm
        action={{
          label: 'Настройки рабочего пространства',
          path: '/api/v1/workspace',
          method: 'PATCH',
          initial: row,
        }}
        onClose={() => void navigate({ to: '/admin/workspace' })}
        onSuccess={() => {
          void workspace.refetch();
          void navigate({ to: '/admin/workspace' });
        }}
      />
    ) : (
      <Empty title="Недостаточно прав" />
    );
  return (
    <section className="form-page workspace-settings">
      <section className="panel details-list">
        <div>
          <span>Название</span>
          <span>{row.name}</span>
        </div>
        <div>
          <span>Идентификатор для запросов кандидатов</span>
          <span>{row.slug}</span>
        </div>
        <div>
          <span>Часовой пояс</span>
          <span>{row.timezone}</span>
        </div>
        <div>
          <span>Язык по умолчанию</span>
          <span>
            {row.defaultLocale.toLowerCase().startsWith('ru') ? 'Русский' : row.defaultLocale}
          </span>
        </div>
      </section>
      {access.can('workspace.manage') && (
        <Link className="pill" to="/admin/workspace/edit">
          Изменить
        </Link>
      )}
    </section>
  );
}
